import { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Select, SelectItem } from 'team-hero-ui';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useNavigate } from 'react-router';

import { IFormHeaderProps } from './FormHeader.types';
import {
  DescriptionWrapperStyled,
  DropdownWrapperStyled,
  FormHeaderImageStyled,
  LogoWrapperStyled,
  StaticComponentsStyled,
} from './FormHeader.styled';
import ModalValidationWrapper from 'components/Modals/AddEditModal/ModalValidationWrapper';

const FormHeader: FC<IFormHeaderProps> = ({
  jobAds,
  logoUrl,
  control,
  selectedJobAd,
  sourceTitle,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('form');

  const getLogoUrlBasePath = (): string => {
    if (
      import.meta.env.MODE === 'development' ||
      import.meta.env.MODE === 'mobile'
    ) {
      return import.meta.env.VITE_API_URI || '';
    }
    return '';
  };

  const selectItems = useMemo(
    () =>
      jobAds
        .filter((jobAd) => !jobAd.isPrivate)
        .map((jobAd) => {
          const value = jobAd.id;
          return (
            <SelectItem key={value} value={value}>
              {jobAd.title}
            </SelectItem>
          );
        }),
    [jobAds]
  );

  const description = useMemo(
    () =>
      jobAds.find((item) => item.id === selectedJobAd?.id)?.description || '',
    [jobAds, selectedJobAd?.id]
  );

  const onJobAdChange = (value: string | number) => {
    navigate({
      pathname: `/job/${value}`,
      search: sourceTitle ? `?source=${sourceTitle}` : '',
    });
  };

  return (
    <StaticComponentsStyled>
      <LogoWrapperStyled>
        {!!logoUrl && (
          <FormHeaderImageStyled
            alt={'Logo'}
            src={getLogoUrlBasePath() + logoUrl}
          />
        )}
      </LogoWrapperStyled>
      {!selectedJobAd?.isPrivate && (
        <DropdownWrapperStyled>
          <Controller
            key={'jobAd'}
            name={'jobAd'}
            defaultValue={selectedJobAd?.id.toString()}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('validation.requiredField'),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ModalValidationWrapper text={error?.message}>
                <Select
                  label={t('field.selectApplication') + '*'}
                  value={value}
                  onChange={(newValue) => {
                    onChange(newValue);
                    onJobAdChange(newValue);
                  }}
                  styleType={'formUniversal'}
                >
                  {selectItems}
                </Select>
              </ModalValidationWrapper>
            )}
          />
        </DropdownWrapperStyled>
      )}
      <DescriptionWrapperStyled>
        <pre>
          <ReactMarkdown rehypePlugins={[rehypeRaw]} children={description} />
        </pre>
      </DescriptionWrapperStyled>
    </StaticComponentsStyled>
  );
};

export default FormHeader;
